import { useEffect, useRef, useState, type ReactNode } from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Peças do Armazém pensadas para o telemóvel (Fase 2c, L4): barra fixa em
 * baixo com a ação principal, folha inferior, separadores e zoom por gesto.
 * Alvos de toque ≥ 48 px, texto ≥ 14 px, áreas seguras respeitadas.
 */

/** `true` a partir de `minWidth` px (ex.: 1024 → lado a lado). */
export function useMinWidth(minWidth: number): boolean {
  const query = `(min-width: ${minWidth}px)`
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : true))
  useEffect(() => {
    const mq = window.matchMedia(query)
    const on = () => setMatches(mq.matches)
    on()
    mq.addEventListener("change", on)
    return () => mq.removeEventListener("change", on)
  }, [query])
  return matches
}

/**
 * Barra fixa em baixo, ao alcance do polegar, com a ação principal do ecrã.
 * A página deixa espaço com `<ActionBarSpacer />`.
 */
export function ActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className={cn("mx-auto flex max-w-3xl items-center gap-2 px-3 py-2", className)}>{children}</div>
    </div>
  )
}

export function ActionBarSpacer() {
  return <div className="h-24" aria-hidden />
}

/** Folha que sobe do fundo (telemóvel); no computador fica centrada. */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-background p-4 shadow-xl sm:max-w-md sm:rounded-2xl"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-muted-foreground/30 sm:hidden" />
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex-1 text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Fechar" className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Separadores grandes (telemóvel). */
export function Segmented<K extends string>({
  value,
  onChange,
  items,
}: {
  value: K
  onChange: (k: K) => void
  items: Array<{ key: K; label: ReactNode }>
}) {
  return (
    // Largura pela do texto (flex-auto): quatro separadores cabem a 360 px sem cortar.
    <div role="tablist" className="flex gap-1 rounded-xl bg-muted p-1">
      {items.map((i) => (
        <button
          key={i.key}
          role="tab"
          type="button"
          aria-selected={value === i.key}
          onClick={() => onChange(i.key)}
          className={cn(
            "h-12 min-w-0 flex-auto truncate rounded-lg font-medium",
            // Com quatro separadores no telemóvel, 14 px e margens curtas para caberem.
            items.length > 3 ? "px-1 text-sm" : "px-2 text-[15px]",
            value === i.key ? "bg-background shadow-sm" : "text-muted-foreground",
          )}
        >
          {i.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Imagem com zoom por gesto (dois dedos), arrastar quando ampliada e toque
 * duplo para 2×/1×. Na iframe o gesto do browser amplia a página toda — aqui
 * amplia só o documento.
 */
export function PinchZoomImage({ src, alt }: { src: string; alt: string }) {
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 })
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const start = useRef<{ dist: number; scale: number; x: number; y: number; px: number; py: number } | null>(null)
  const lastTap = useRef(0)

  const dist = () => {
    const [a, b] = [...pointers.current.values()]
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
  }

  return (
    <div
      className="relative h-full w-full touch-none overflow-hidden bg-muted"
      onPointerDown={(e) => {
        ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        start.current = { dist: dist(), scale: t.scale, x: t.x, y: t.y, px: e.clientX, py: e.clientY }
        if (pointers.current.size === 1) {
          const now = Date.now()
          if (now - lastTap.current < 300) setT((c) => (c.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2, x: 0, y: 0 }))
          lastTap.current = now
        }
      }}
      onPointerMove={(e) => {
        if (!pointers.current.has(e.pointerId) || !start.current) return
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        const s = start.current
        if (pointers.current.size >= 2 && s.dist > 0) {
          setT((c) => ({ ...c, scale: Math.min(6, Math.max(1, (s.scale * dist()) / s.dist)) }))
        } else if (pointers.current.size === 1 && t.scale > 1) {
          setT((c) => ({ ...c, x: s.x + e.clientX - s.px, y: s.y + e.clientY - s.py }))
        }
      }}
      onPointerUp={(e) => {
        pointers.current.delete(e.pointerId)
        start.current = pointers.current.size ? { dist: dist(), scale: t.scale, x: t.x, y: t.y, px: e.clientX, py: e.clientY } : null
        if (t.scale <= 1) setT({ scale: 1, x: 0, y: 0 })
      }}
      onPointerCancel={(e) => pointers.current.delete(e.pointerId)}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="h-full w-full select-none object-contain"
        style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`, transformOrigin: "center" }}
      />
    </div>
  )
}
